import { Questionnaire } from '../../../types/questionnaire/types';
import { buildApiUrl } from '../../../config/api';

interface CreateSessionResponse {
  sessionId: string;
  questionnaireId: string;
}

export interface DrugSku {
  skuId: string;
  strengthMg: number;
  quantity: number;
  frequency: number;
  priceMinor: number;
  currency: string;
}

interface DrugSkusResponse {
  drugId: string;
  skus: DrugSku[];
}

export const fetchQuestionnaire = async (questionnaireId: string): Promise<Questionnaire> => {
  const url = buildApiUrl(`/questionnaires/${questionnaireId}`);
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(`Failed to fetch questionnaire: ${resp.status} ${resp.statusText}`);
  }
  return (await resp.json()) as Questionnaire;
};

export const fetchDrugSkus = async (drugId: string): Promise<DrugSkusResponse> => {
  const url = buildApiUrl(`/catalog/drugs/${drugId}/skus`);
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(`Failed to fetch drug skus: ${resp.status} ${resp.statusText}`);
  }
  return (await resp.json()) as DrugSkusResponse;
};

export const createQuestionnaireSession = async (questionnaireId: string): Promise<CreateSessionResponse> => {
  const url = buildApiUrl('/questionnaire-sessions');
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ questionnaireId }),
  });
  if (!resp.ok) {
    throw new Error(`Failed to create questionnaire session: ${resp.status} ${resp.statusText}`);
  }
  return (await resp.json()) as CreateSessionResponse;
};

export const completeQuestionnaireSession = async (params: {
  sessionId: string;
  questionnaireId: string;
  answers: Record<string, unknown>;
  recommendedDrugId?: string;
}): Promise<void> => {
  const { sessionId, questionnaireId, answers, recommendedDrugId } = params;
  const url = buildApiUrl(`/questionnaire-sessions/${sessionId}/complete`);
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ questionnaireId, answers, recommendedDrugId }),
  });
  if (!resp.ok) {
    throw new Error(`Failed to complete questionnaire session: ${resp.status} ${resp.statusText}`);
  }
  return;
};

export const saveQuestionnaireSelection = async (params: { token: string; skuId: string }): Promise<void> => {
  const { token, skuId } = params;
  const url = buildApiUrl(`/questionnaire/${token}/selection`);
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ skuId }),
  });
  if (!resp.ok) {
    throw new Error(`Failed to save questionnaire selection: ${resp.status} ${resp.statusText}`);
  }
};

export const attachQuestionnaireSession = async (params: {
  sessionToken: string;
  profile: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    phone: string;
  };
  authToken: string;
}): Promise<void> => {
  const { sessionToken, profile, authToken } = params;
  const url = buildApiUrl(`/questionnaire-sessions/${sessionToken}/attach`);
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ profile }),
  });
  if (!resp.ok) {
    throw new Error(`Failed to attach questionnaire session: ${resp.status} ${resp.statusText}`);
  }
};

export const getShippingAddresses = async (params: { authToken: string }) => {
  const url = buildApiUrl('/me/shipping-addresses');
  const resp = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${params.authToken}`,
    },
  });
  if (!resp.ok) {
    throw new Error(`Failed to load shipping addresses: ${resp.status} ${resp.statusText}`);
  }
  return (await resp.json()) as {
    defaultShippingAddressId: string | null;
    shippingAddresses: Array<{
      addressId: string;
      shippingAddress: {
        streetAndNumber: string;
        postalCode: string;
        city: string;
        region?: string;
        country: string;
        givenName: string;
        familyName: string;
        email?: string;
      };
      createdAt: string;
      updatedAt: string;
    }>;
  };
};

type ShippingAddressPayload = {
  streetAndNumber: string;
  postalCode: string;
  city: string;
  region?: string;
  country: string;
  givenName: string;
  familyName: string;
  email?: string;
};

export const createShippingAddress = async (params: {
  authToken: string;
  shippingAddress: ShippingAddressPayload;
  setAsDefault?: boolean;
}) => {
  const url = buildApiUrl('/me/shipping-addresses');
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.authToken}`,
    },
    body: JSON.stringify({
      shippingAddress: params.shippingAddress,
      setAsDefault: params.setAsDefault ?? false,
    }),
  });
  if (!resp.ok) {
    let payload: { errors?: Array<{ field: string; message: string }>; message?: string } | undefined;
    try {
      payload = (await resp.json()) as typeof payload;
    } catch (error) {
      payload = undefined;
    }
    const errorMessage = payload?.message || `Failed to create shipping address: ${resp.status} ${resp.statusText}`;
    const err = new Error(errorMessage) as Error & { errors?: Array<{ field: string; message: string }> };
    if (payload?.errors) {
      err.errors = payload.errors;
    }
    throw err;
  }
  return (await resp.json()) as {
    addressId: string;
    shippingAddress: ShippingAddressPayload;
    createdAt: string;
  };
};
